<template>
  <div class="chat-wrapper">
    <!-- Header -->
    <div class="chat-header">
      <div class="chat-header__info">
        <div class="chat-header__avatar">
          <img
            v-if="claim.client?.logo && !logoError"
            :src="claim.client?.logo"
            :alt="claim.client?.store"
            class="avatar-img"
            @error="logoError = true"
          />
          <div v-else class="avatar-letter avatar-letter--lg" :style="{ background: getColor(claim.client?.store) }">
            {{ firstLetter(claim.client?.store) }}
          </div>
          <span class="avatar-status"></span>
        </div>
        <div class="chat-header__meta">
          <span class="chat-header__name">
            {{ claim.client?.store }}
          </span>
          <span class="chat-header__sub">{{ claim.client?.store }}</span>
        </div>
      </div>
      <div class="chat-header__actions">
        <span class="claim-badge">Réclamation #{{ claim.id }}</span>
      </div>
    </div>

    <!-- Messages area -->
    <div class="chat-body" ref="chatBody">
      <div class="chat-date-separator">
        <span>Conversation</span>
      </div>

      <template v-for="(item, index) in messages" :key="index">
        <!-- Received -->
        <div v-if="item.from != 2" class="bubble-row bubble-row--left">
          <img
            v-if="user?.pic && !userImgError"
            :src="user?.pic"
            class="bubble-avatar"
            :alt="user?.name"
            @error="userImgError = true"
          />
          <div v-else class="avatar-letter avatar-letter--sm" :style="{ background: getColor(user?.name) }">
            {{ firstLetter(user?.name) }}
          </div>
          <div class="bubble-col">
            <span class="bubble-sender">{{ user?.name }}</span>
            <div class="bubble bubble--received">
              <p>{{ item.message }}</p>
              <span class="bubble-time">{{ item.time }}</span>
            </div>
          </div>
        </div>

        <!-- Sent -->
        <div v-else class="bubble-row bubble-row--right">
          <div class="bubble bubble--sent">
            <p>{{ item.message }}</p>
            <span class="bubble-time">
              {{ item.time }}
              <i class="ri-check-double-line bubble-check"></i>
            </span>
          </div>
        </div>
      </template>

      <div ref="messagesEnd"></div>
    </div>

    <!-- Input area -->
    <div class="chat-footer">
      <input
        type="text"
        v-model="chatInputMessage"
        class="chat-input"
        placeholder="Saisir votre message..."
        @keyup.enter="sendMessage"
      />
      <button
        class="send-btn"
        :class="{ 'send-btn--active': chatInputMessage.trim() }"
        @click="sendMessage"
        :disabled="!chatInputMessage.trim()"
      >
        <i class="flaticon-send"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { ref, watch, nextTick } from "vue";

export default {
  name: "ChatClaim",
  props: {
    messages: {
      type: Array,
      required: true,
    },
    fetchMessages: {
      type: Function,
      required: true,
    },
    rowClaim: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const auth = useAuthStore();
    const user = auth.user;
    const chatInputMessage = ref("");
    const messagesEnd = ref(null);
    const chatBody = ref(null);
    const userImgError = ref(false);
    const logoError = ref(false);

    function firstLetter(name) {
      return name ? name.charAt(0).toUpperCase() : "?";
    }

    const COLORS = ["#1abc9c","#3498db","#9b59b6","#e67e22","#e74c3c","#2ecc71","#f39c12","#16a085","#8e44ad","#d35400"];
    function getColor(name) {
      if (!name) return "#6c757d";
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return COLORS[Math.abs(hash) % COLORS.length];
    }

    function scrollToBottom() {
      nextTick(() => {
        if (messagesEnd.value) {
          messagesEnd.value.scrollIntoView({ behavior: "smooth" });
        }
      });
    }

    watch(() => props.messages, scrollToBottom, { deep: true });

    async function sendMessage() {
      if (!chatInputMessage.value.trim()) return;
      const data = new FormData();
      data.append("message", chatInputMessage.value);
      data.append("claim_id", props.rowClaim.id);
      data.append("user_id", user?.id);

      const res = await api.post(`/api/auth/sendMessage`, data);
      if (res.data.msg === "success") {
        props.fetchMessages(props.rowClaim.id);
      }
      chatInputMessage.value = "";
    }

    return {
      claim: props.rowClaim,
      sendMessage,
      chatInputMessage,
      user,
      messagesEnd,
      chatBody,
      userImgError,
      logoError,
      firstLetter,
      getColor,
    };
  },
};
</script>

<style scoped>
/* ── Wrapper ── */
.chat-wrapper {
  display: flex;
  flex-direction: column;
  height: 600px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.10);
  background: #fff;
}

/* ── Header ── */
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: #075e54;
  color: #fff;
  flex-shrink: 0;
}
.chat-header__info {
  display: flex;
  align-items: center;
  gap: 12px;
}
.chat-header__avatar {
  position: relative;
  flex-shrink: 0;
}
.avatar-img {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255,255,255,0.4);
}
.avatar-letter {
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.avatar-letter--lg {
  width: 42px;
  height: 42px;
  font-size: 18px;
  border: 2px solid rgba(255,255,255,0.4);
}
.avatar-letter--sm {
  width: 30px;
  height: 30px;
  font-size: 13px;
  margin-bottom: 2px;
}
.avatar-status {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  background: #25d366;
  border-radius: 50%;
  border: 2px solid #075e54;
}
.chat-header__name {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}
.chat-header__sub {
  display: block;
  font-size: 12px;
  color: rgba(255,255,255,0.75);
}
.claim-badge {
  background: rgba(255,255,255,0.15);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.3);
  white-space: nowrap;
}

/* ── Messages body ── */
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
  background: #e5ddd5 url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c5bdb5' fill-opacity='0.25'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.chat-body::-webkit-scrollbar { width: 5px; }
.chat-body::-webkit-scrollbar-track { background: transparent; }
.chat-body::-webkit-scrollbar-thumb { background: #b0aaa3; border-radius: 10px; }

/* ── Date separator ── */
.chat-date-separator {
  text-align: center;
  margin: 8px 0 12px;
}
.chat-date-separator span {
  background: rgba(255,255,255,0.85);
  color: #6c757d;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 12px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

/* ── Bubble rows ── */
.bubble-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 75%;
}
.bubble-row--left  { align-self: flex-start; }
.bubble-row--right { align-self: flex-end; flex-direction: row-reverse; }

.bubble-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  margin-bottom: 2px;
}
.bubble-col { display: flex; flex-direction: column; gap: 2px; }
.bubble-sender {
  font-size: 11px;
  font-weight: 600;
  color: #075e54;
  padding-left: 4px;
}

/* ── Bubble ── */
.bubble {
  display: inline-flex;
  flex-direction: column;
  padding: 8px 10px 6px;
  border-radius: 8px;
  max-width: 100%;
  box-shadow: 0 1px 2px rgba(0,0,0,0.15);
  word-break: break-word;
}
.bubble p {
  margin: 0 0 4px;
  font-size: 14px;
  line-height: 1.5;
  color: #111;
}
.bubble--received {
  background: #fff;
  border-top-left-radius: 0;
}
.bubble--sent {
  background: #dcf8c6;
  border-bottom-right-radius: 0;
}
.bubble-time {
  align-self: flex-end;
  font-size: 10px;
  color: #999;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
  line-height: 1;
}
.bubble-check {
  color: #53bdeb;
  font-size: 13px;
}

/* ── Footer input ── */
.chat-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f0f0f0;
  border-top: 1px solid #ddd;
  flex-shrink: 0;
}
.chat-input {
  flex: 1;
  border: none;
  border-radius: 24px;
  padding: 10px 18px;
  font-size: 14px;
  background: #fff;
  outline: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  color: #111;
}
.chat-input::placeholder { color: #aaa; }

.send-btn {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  background: #075e54;
  color: #fff;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, opacity 0.15s;
  flex-shrink: 0;
  opacity: 0.45;
}
.send-btn--active {
  opacity: 1;
}
.send-btn--active:hover {
  background: #128c7e;
  transform: scale(1.08);
}
</style>
